import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { BrowserRouter as Router, Route, Routes, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { UserProfile, fetchUserProfile, isAdminUser } from './services/api';
import ProLanding from './components/ProLanding';
import ProWelcomePreview from './components/ProWelcomePreview';
import HomePage from './components/HomePage';
import VenuePage from './components/VenuePage';
import AlertSettings from './components/AlertSettings';
import UserDashboardPage from './components/UserDashboardPage';
import Login from './components/Login';
import Footer from './components/Footer';
import PrivacyPolicy from './components/Footer/PrivacyPolicy';
import TermsOfService from './components/Footer/TermsOfService';
import { User, ChevronDown, LogOut, Trash2 } from 'lucide-react';
import Logo from './components/Logo';
import DeleteAccountModal from './components/DeleteAccountModal';
import { deleteUserAccount } from './services/api';
import UserMenu from './components/UserMenu';
import Analytics from './services/analytics';
import AdminDashboard from './components/AdminDashboard';
import ScraperDashboard from './components/ScraperDashboard';
import ProAccessAdmin from './components/ProAccessAdmin';
import NotFound from './components/NotFound';
import OpenTableAvailability from './components/OpenTableAvailability';
import { SubscriptionProvider, ProtectedFeature } from './components/SubscriptionFeatures';
import HeaderNav, { MobileHeaderNav } from './components/HeaderNav';
import { PaymentSuccess, PaymentCancel } from './components/PaymentComponents';
import EmailConsentModal from './components/EmailConsentModal';

const RestaurantMap = React.lazy(() => import('./components/RestaurantMap'));

const HeaderButton: React.FC = () => {
  const navigate = useNavigate();
  return (
    <button onClick={() => navigate('/')} className="text-2xl sm:text-3xl font-bold text-ink-900">
      <Logo />
    </button>
  );
};

const RouteTracker: React.FC = () => {
  const location = useLocation();
  const previousPath = useRef<string>('');

  useEffect(() => {
    // Only track if it's a new path
    if (location.pathname !== previousPath.current) {
      Analytics.pageview(location.pathname);
      previousPath.current = location.pathname;
    }
  }, [location]);

  return null;
};



/**
 * Gate for staff-only routes.
 *
 * The profile is fetched after first paint, so `userProfile` is briefly null
 * even for an admin. Redirecting on that null bounced admins straight home on
 * any direct visit or hard refresh - wait for the profile to resolve first.
 */
const AdminRoute: React.FC<{
  userProfile: UserProfile | null;
  isLoading: boolean;
  children: React.ReactNode;
}> = ({ userProfile, isLoading, children }) => {
  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-500" />
      </div>
    );
  }
  return isAdminUser(userProfile) ? <>{children}</> : <Navigate to="/" replace />;
};

const App: React.FC = () => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const [isProTourOpen, setIsProTourOpen] = useState(false);
  const [showConsent, setShowConsent] = useState(false);


    // Initialize Analytics
    useEffect(() => {
      Analytics.initialize();
    }, []);  

  const setUserProfileCallback = useCallback((newProfile: React.SetStateAction<UserProfile | null>) => {
    // console.log('Setting new user profile:', newProfile);
    setUserProfile(newProfile);
  }, []);

  useEffect(() => {
    const loadUserProfile = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const profile = await fetchUserProfile();
          setUserProfileCallback(profile);
        } catch (error) {
          console.error('Error fetching user profile:', error);
          localStorage.removeItem('token');
          setUserProfileCallback(null);
        }
      }
      setIsProfileLoading(false);
    };
    loadUserProfile();
  }, [setUserProfileCallback]);

  useEffect(() => {
    // Show consent dialog if logged in and hasn't been shown yet
    if (userProfile && userProfile.email_consent_shown === false) {
      // Small delay to not show immediately after login
      const timer = setTimeout(() => setShowConsent(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [userProfile]);
  

  const handleLogout = () => {
    Analytics.logAuth.signOut();  // Updated from Analytics.logLogout()
    localStorage.removeItem('token');
    localStorage.removeItem('googleFirstName');
    setUserProfileCallback(null);
    setShowConsent(false);
  };

  

// The return statement in your App component
return (
  <GoogleOAuthProvider clientId="260065421156-6bor1a5hr1pkmhh42kqus63b6njbcjse.apps.googleusercontent.com">
    <SubscriptionProvider userId={userProfile?.user.id}>
      <Router>
        <RouteTracker />
        {/* Flat warm ground for the discovery palette. The animated blue wash
            that used to sit here fought the cream/sage cards on the home page,
            so the page provides its own quiet background instead. */}
        <div className="relative min-h-screen bg-paper flex flex-col">
          <header className="relative z-30 bg-surface bg-opacity-95 shadow-sm border-b border-ink-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center h-16 relative">
                {/* Left side with logo and nav */}
                <div className="flex items-center space-x-8">
                  <Logo />
                  <div className="hidden md:block">
                    <HeaderNav />
                  </div>
                </div>

                {/* Right side with user menu / sign-in */}
                <div className="flex items-center space-x-1">
                  {userProfile ? (
                    <UserMenu
                      userProfile={userProfile}
                      onLogout={handleLogout}
                    />
                  ) : (
                    <Login
                      key="logged-out"
                      userProfile={null}
                      setUserProfile={setUserProfileCallback}
                    />
                  )}
                </div>
              </div>

              {/* Mobile: the two sections, in the open under the logo rather
                  than behind a hamburger. Hidden from md up - the desktop
                  header already shows them beside the logo. */}
              <MobileHeaderNav />
            </div>
          </header>

          <main className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12 flex-grow">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route 
                path="/venues/:city" 
                element={
                  <VenuePage 
                    userProfile={userProfile} 
                    setUserProfile={setUserProfileCallback} 
                  />
                } 
              />
              <Route
                path="/dashboard"
                element={<UserDashboardPage userProfile={userProfile} />}
              />
              <Route path="/alerts" element={
                isProfileLoading ? <p role="status">Loading your alerts…</p> :
                <AlertSettings userProfile={userProfile} setUserProfile={setUserProfileCallback} />
              } />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route 
                path="/availability" 
                element={
                  <ProtectedFeature userProfile={userProfile} setUserProfile={setUserProfileCallback}>
                    <OpenTableAvailability userProfile={userProfile} setUserProfile={setUserProfileCallback} onTourActiveChange={setIsProTourOpen} />
                  </ProtectedFeature>
                } 
              />
              <Route
                path="/dashboard/admin"
                element={
                  <AdminRoute userProfile={userProfile} isLoading={isProfileLoading}>
                    <AdminDashboard />
                  </AdminRoute>
                }
              />
              <Route path="/dashboard/admin/map" element={
                <AdminRoute userProfile={userProfile} isLoading={isProfileLoading}>
                  <React.Suspense fallback={<p role="status">Loading map…</p>}><RestaurantMap /></React.Suspense>
                </AdminRoute>
              } />
              <Route
                path="/dashboard/admin/subscribers"
                element={
                  <AdminRoute userProfile={userProfile} isLoading={isProfileLoading}>
                    <ProAccessAdmin />
                  </AdminRoute>
                }
              />
              <Route path="/dashboard/admin/users" element={<Navigate to="/dashboard/admin/subscribers" replace />} />
              <Route
                path="/dashboard/scrapers"
                element={
                  <AdminRoute userProfile={userProfile} isLoading={isProfileLoading}>
                    <ScraperDashboard />
                  </AdminRoute>
                }
              />
              <Route path="/payment/success" element={<PaymentSuccess onProfileUpdate={setUserProfileCallback} />} />
              {process.env.NODE_ENV === 'development' && <Route path="/dev/pro-offer" element={<ProLanding userProfile={null} setUserProfile={setUserProfileCallback} />} />}
              {process.env.NODE_ENV === 'development' && <Route path="/dev/pro-welcome" element={<ProWelcomePreview onTourActiveChange={setIsProTourOpen} />} />}
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              {/* Must stay last: anything unmatched lands here rather than
                  rendering an empty page. */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>

          <Footer />
          {showConsent && userProfile && !isProTourOpen && (
            <EmailConsentModal 
              onClose={() => setShowConsent(false)}
              onConsent={(consented) => {  // Change to onConsent and handle the boolean parameter
                // Update local state to prevent showing the modal again
                if (userProfile) {
                  setUserProfileCallback({
                    ...userProfile,
                    email_consent: consented,  // Use the consented value
                    email_consent_shown: true
                  });
                }
              }}
            />
          )}

        </div>
      </Router>
    </SubscriptionProvider>
  </GoogleOAuthProvider>
);
};

export default App;
